import { useState, useEffect, useCallback, Component } from "react";
import { Link } from "react-router-dom";
import apiFetch from "../../../shared/services/apiClient";
import { setMultimonedaFlag } from "../../../shared/hooks/useMultimoneda";

// ── Error Boundary ────────────────────────────────────────────────────────────
class AdminErrorBoundary extends Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error("[AdminPage] Error capturado:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: "40px 24px", background: "rgba(239,68,68,0.06)",
          border: "1px solid rgba(239,68,68,0.2)", borderRadius: 12,
          color: "#fca5a5", fontFamily: "'Inter',sans-serif",
        }}>
          <div style={{ fontSize: "1.5rem", marginBottom: 12 }}>⚠️ Error al cargar el panel</div>
          <pre style={{ fontSize: "0.78rem", color: "#f87171", overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
            {this.state.error?.message}
            {"\n\n"}
            {this.state.error?.stack}
          </pre>
          <button onClick={() => this.setState({ hasError: false, error: null })}
            style={{ marginTop: 16, padding: "8px 18px", background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 8, color: "#f87171", cursor: "pointer", fontSize: "0.85rem" }}>
            Reintentar
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  bg0: "#0f172a", bg1: "#131929", bg2: "#1a2235", bg3: "#1e2a3a",
  border: "rgba(255,255,255,0.07)", accent: "#f97316",
  text: "#f1f5f9", muted: "#64748b",
};

// ── Primitives ────────────────────────────────────────────────────────────────
function Input({ label, required, error, type = "text", ...props }) {
  const [focus, setFocus] = useState(false);
  return (
    <div style={{ marginBottom: 14 }}>
      {label && (
        <label style={{
          display: "block", fontSize: "0.7rem", fontWeight: 700,
          letterSpacing: "0.6px", textTransform: "uppercase", marginBottom: 5,
          color: error ? "#f87171" : C.muted,
        }}>
          {label}{required && <span style={{ color: "#f87171", marginLeft: 3 }}>*</span>}
        </label>
      )}
      <input type={type} {...props}
        onFocus={e => { setFocus(true); props.onFocus?.(e); }}
        onBlur={e => { setFocus(false); props.onBlur?.(e); }}
        style={{
          width: "100%", background: C.bg1, borderRadius: 8,
          border: `1px solid ${error ? "#f87171" : focus ? C.accent : C.border}`,
          padding: "10px 14px", color: C.text, fontSize: "0.88rem",
          outline: "none", boxSizing: "border-box", transition: "border-color 0.15s",
        }} />
      {error && <div style={{ color: "#f87171", fontSize: "0.75rem", marginTop: 4 }}>{error}</div>}
    </div>
  );
}

function Select({ label, required, error, children, ...props }) {
  return (
    <div style={{ marginBottom: 14 }}>
      {label && (
        <label style={{
          display: "block", fontSize: "0.7rem", fontWeight: 700,
          letterSpacing: "0.6px", textTransform: "uppercase", marginBottom: 5,
          color: error ? "#f87171" : C.muted,
        }}>
          {label}{required && <span style={{ color: "#f87171", marginLeft: 3 }}>*</span>}
        </label>
      )}
      <select {...props} style={{
        width: "100%", background: C.bg1, borderRadius: 8,
        border: `1px solid ${error ? "#f87171" : C.border}`,
        padding: "10px 14px", color: C.text, fontSize: "0.88rem",
        outline: "none", boxSizing: "border-box",
      }}>
        {children}
      </select>
      {error && <div style={{ color: "#f87171", fontSize: "0.75rem", marginTop: 4 }}>{error}</div>}
    </div>
  );
}

function Btn({ children, onClick, variant = "primary", disabled, small, type = "button" }) {
  const [h, setH] = useState(false);
  const cfg = {
    primary: { bg: h ? "#ea6b10" : "#f97316", color: "#fff", border: "#f97316" },
    ghost:   { bg: h ? "rgba(255,255,255,0.05)" : "transparent", color: C.muted, border: C.border },
    danger:  { bg: h ? "rgba(239,68,68,0.2)" : "rgba(239,68,68,0.08)", color: "#f87171", border: "rgba(239,68,68,0.3)" },
    success: { bg: h ? "#059669" : "#10b981", color: "#fff", border: "#10b981" },
  }[variant] ?? {};
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      onMouseOver={() => setH(true)} onMouseOut={() => setH(false)}
      style={{
        background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: 8,
        color: cfg.color, padding: small ? "5px 12px" : "9px 18px",
        fontSize: small ? "0.78rem" : "0.85rem", fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
        transition: "all 0.15s", display: "inline-flex", alignItems: "center",
        gap: 7, whiteSpace: "nowrap", fontFamily: "'Inter',sans-serif",
      }}>
      {children}
    </button>
  );
}

function Toast({ msg, type, onClose }) {
  if (!msg) return null;
  const ok = type !== "error";
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      background: ok ? "rgba(16,185,129,0.08)" : "rgba(239,68,68,0.08)",
      border: `1px solid ${ok ? "rgba(16,185,129,0.25)" : "rgba(239,68,68,0.25)"}`,
      borderLeft: `3px solid ${ok ? "#10b981" : "#ef4444"}`,
      borderRadius: 8, padding: "10px 16px", marginBottom: 16,
      color: ok ? "#6ee7b7" : "#fca5a5", fontSize: "0.84rem",
    }}>
      <i className={`fa-solid ${ok ? "fa-circle-check" : "fa-circle-exclamation"}`} />
      <span style={{ flex: 1 }}>{msg}</span>
      <button onClick={onClose} style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", fontSize: "0.9rem", padding: 0 }}>
        <i className="fa-solid fa-xmark" />
      </button>
    </div>
  );
}

// Banner de error persistente — usado cuando una carga falla, para que el motivo
// real (403/500/sin sesión) sea visible en vez de mostrar una pestaña vacía.
function ErrorBanner({ msg, onRetry }) {
  if (!msg) return null;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)",
      borderLeft: "3px solid #ef4444", borderRadius: 8, padding: "12px 16px",
      marginBottom: 16, color: "#fca5a5", fontSize: "0.84rem",
    }}>
      <i className="fa-solid fa-circle-exclamation" />
      <span style={{ flex: 1 }}>{msg}</span>
      {onRetry && (
        <button onClick={onRetry} style={{
          background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.3)",
          borderRadius: 6, color: "#f87171", cursor: "pointer", fontSize: "0.78rem",
          padding: "4px 12px", fontWeight: 600,
        }}>
          <i className="fa-solid fa-rotate-right" style={{ marginRight: 5 }} />Reintentar
        </button>
      )}
    </div>
  );
}

// Lee la respuesta como JSON, lanzando un Error descriptivo si el status no es OK.
async function readJsonOrThrow(res, label) {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = body.detail || body.error ||
      (res.status === 403 ? "No tenés permisos de superadministrador." : `Error ${res.status}`);
    throw new Error(`${label}: ${detail}`);
  }
  return res.json();
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(0,0,0,0.7)", backdropFilter: "blur(3px)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }} onClick={onClose}>
      <div style={{
        background: C.bg2, border: `1px solid ${C.border}`,
        borderRadius: 12, width: wide ? "min(680px,97vw)" : "min(540px,95vw)",
        maxHeight: "90vh", overflow: "auto", padding: 28,
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h3 style={{ margin: 0, color: C.text, fontSize: "1.1rem", fontFamily: "'Manrope',sans-serif", fontWeight: 700 }}>{title}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: "1.1rem" }}>
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Multimoneda Toggle ────────────────────────────────────────────────────────
function MultimonedaToggle({ c, onToggle }) {
  const active = !!c.multimoneda_activa;
  const [h, setH] = useState(false);
  return (
    <button type="button"
      title={active ? "Desactivar multimoneda" : "Activar multimoneda (USD)"}
      onClick={() => onToggle(!active)}
      onMouseOver={() => setH(true)} onMouseOut={() => setH(false)}
      style={{
        background: active
          ? h ? "rgba(96,165,250,0.25)" : "rgba(96,165,250,0.12)"
          : h ? "rgba(255,255,255,0.07)" : "transparent",
        border: `1px solid ${active ? "rgba(96,165,250,0.4)" : C.border}`,
        borderRadius: 7, padding: "4px 10px",
        color: active ? "#60a5fa" : C.muted,
        fontSize: "0.75rem", fontWeight: 600,
        cursor: "pointer", transition: "all 0.15s",
        display: "inline-flex", alignItems: "center", gap: 5,
      }}>
      <i className="fa-solid fa-dollar-sign" />
      {active ? "USD ON" : "USD OFF"}
    </button>
  );
}

// ── Condominios Tab ───────────────────────────────────────────────────────────
function CondominiosTab() {
  const [condominios, setCond] = useState([]);
  const [loading, setLoading]  = useState(true);
  const [modal, setModal]      = useState(null);
  const [form, setForm]        = useState({});
  const [errors, setErrors]    = useState({});
  const [saving, setSaving]    = useState(false);
  const [toast, setToast]      = useState({ msg: "", type: "" });
  const [deleting, setDeleting] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [confirmReset, setConfirmReset] = useState(null);
  const [resetText, setResetText] = useState("");
  const [resetting, setResetting] = useState(false);
  const [loadErr, setLoadErr]   = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setLoadErr("");
    apiFetch("/api/core/admin/condominios/")
      .then(r => readJsonOrThrow(r, "Condominios"))
      .then(d => setCond(Array.isArray(d) ? d : []))
      .catch(e => setLoadErr(e.message || "Error al cargar condominios"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: "", type: "" }), 5000);
  };

  function openCreate() {
    setForm({ cedulaJuridica: "", nombre: "", email: "", direccion: "" });
    setErrors({});
    setModal("create");
  }

  function openEdit(c) {
    setForm({ ...c });
    setErrors({});
    setModal(c);
  }

  async function handleSave() {
    const errs = {};
    if (!form.cedulaJuridica?.trim()) errs.cedulaJuridica = "Requerido";
    if (!form.nombre?.trim())         errs.nombre         = "Requerido";
    if (!form.email?.trim())          errs.email          = "Requerido";
    if (Object.keys(errs).length)     { setErrors(errs); return; }

    setSaving(true);
    try {
      const isEdit = modal !== "create";
      const url    = isEdit ? `/api/core/admin/condominios/${modal.id}/` : "/api/core/admin/condominios/";
      const method = isEdit ? "PATCH" : "POST";
      const res    = await apiFetch(url, { method, body: JSON.stringify(form) });
      if (!res.ok) {
        const e = await res.json();
        setErrors(typeof e === "object" ? e : { nombre: JSON.stringify(e) });
        return;
      }
      setModal(null);
      load();
      showToast(isEdit ? "Condominio actualizado ✓" : "Condominio creado ✓");
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(c) {
    setDeleting(c.id);
    setConfirmDel(null);
    try {
      const res = await apiFetch(`/api/core/admin/condominios/${c.id}/`, { method: "DELETE" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        showToast(err.detail || "Error al eliminar condominio", "error");
        return;
      }
      load();
      showToast(`"${c.nombre}" eliminado`);
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setDeleting(null);
    }
  }

  async function handleReset() {
    if (!confirmReset || resetText !== confirmReset.nombre) return;
    setResetting(true);
    try {
      const res = await apiFetch(`/api/core/admin/condominios/${confirmReset.id}/reset/`, {
        method: "POST",
        body: JSON.stringify({ confirmacion: resetText }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast(data.detail || "Error al reiniciar el condominio", "error");
        return;
      }
      setConfirmReset(null);
      setResetText("");
      load();
      showToast(data.detail || `"${confirmReset.nombre}" reiniciado desde cero ✓`);
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setResetting(false);
    }
  }

  const f = key => ({ value: form[key] ?? "", onChange: e => setForm(p => ({ ...p, [key]: e.target.value })) });

  return (
    <div>
      <Toast msg={toast.msg} type={toast.type} onClose={() => setToast({ msg: "", type: "" })} />
      <ErrorBanner msg={loadErr} onRetry={load} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <span style={{ color: C.muted, fontSize: "0.85rem" }}>{condominios.length} condominios registrados</span>
        <Btn onClick={openCreate}><i className="fa-solid fa-plus" />Nuevo condominio</Btn>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: C.muted }}>
          <i className="fa-solid fa-circle-notch fa-spin" style={{ color: C.accent, marginRight: 8 }} />Cargando…
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {condominios.map(c => (
            <div key={c.id} style={{
              background: C.bg1, border: `1px solid ${C.border}`,
              borderRadius: 10, padding: "14px 18px",
              display: "flex", alignItems: "center", gap: 14,
            }}>
              <div style={{
                width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.2)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <i className="fa-solid fa-building" style={{ color: C.accent }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: C.text, fontSize: "0.92rem" }}>{c.nombre}</div>
                <div style={{ color: C.muted, fontSize: "0.77rem", marginTop: 2 }}>
                  {c.cedulaJuridica} · {c.email}
                  {c.multimoneda_activa && (
                    <span style={{
                      marginLeft: 8, background: "rgba(96,165,250,0.15)",
                      border: "1px solid rgba(96,165,250,0.3)", borderRadius: 4,
                      padding: "1px 7px", color: "#60a5fa", fontSize: "0.7rem", fontWeight: 600,
                    }}>Multimoneda ✓</span>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <MultimonedaToggle c={c} onToggle={async (newVal) => {
                  if (newVal && !window.confirm(
                    `¿Activar multimoneda en "${c.nombre}"?\n\nDeberá realizar un asiento de ajuste manual para registrar los saldos en dólares existentes.`
                  )) return;
                  if (!newVal && !window.confirm(
                    `¿Desactivar multimoneda en "${c.nombre}"?\n\nLas columnas USD se ocultarán.`
                  )) return;
                  try {
                    const res = await apiFetch(`/api/core/admin/condominios/${c.id}/`, {
                      method: "PATCH",
                      body: JSON.stringify({ multimoneda_activa: newVal }),
                    });
                    if (!res.ok) { showToast("Error al cambiar multimoneda", "error"); return; }
                    // Si el condominio que se editó es el activo del usuario, sincronizar el flag
                    const condominioId = localStorage.getItem("condominioId");
                    if (condominioId && String(c.id) === String(condominioId)) {
                      setMultimonedaFlag(newVal);
                    }
                    load();
                    showToast(`Multimoneda ${newVal ? "activada" : "desactivada"} para "${c.nombre}"`);
                  } catch (e) { showToast(e.message, "error"); }
                }} />
                <Btn small variant="ghost" onClick={() => openEdit(c)}>
                  <i className="fa-solid fa-pencil" />Editar
                </Btn>
                <Btn small variant="ghost" title="Reiniciar desde cero (borra todos los datos)"
                  onClick={() => { setConfirmReset(c); setResetText(""); }}>
                  <i className="fa-solid fa-rotate-left" style={{ color: "#fb923c" }} />
                </Btn>
                <Btn small variant="danger" disabled={deleting === c.id}
                  onClick={() => setConfirmDel(c)}>
                  {deleting === c.id
                    ? <i className="fa-solid fa-circle-notch fa-spin" />
                    : <i className="fa-solid fa-trash" />}
                </Btn>
              </div>
            </div>
          ))}
          {condominios.length === 0 && (
            <div style={{ textAlign: "center", padding: 40, color: C.muted }}>
              <i className="fa-solid fa-building" style={{ fontSize: "2rem", display: "block", marginBottom: 10, opacity: 0.2 }} />
              No hay condominios registrados.
            </div>
          )}
        </div>
      )}

      {/* Modal crear/editar */}
      {modal && (
        <Modal title={modal === "create" ? "Nuevo Condominio" : "Editar Condominio"} onClose={() => setModal(null)}>
          <Input label="Nombre" required {...f("nombre")} error={errors.nombre} placeholder="Residencial Los Pinos" />
          <Input label="Cédula Jurídica" required {...f("cedulaJuridica")} error={errors.cedulaJuridica} placeholder="3-102-000000" />
          <Input label="Email" type="email" {...f("email")} error={errors.email} placeholder="admin@condominio.com" />
          <Input label="Dirección" {...f("direccion")} placeholder="San José, Costa Rica" />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
            <Btn variant="ghost" onClick={() => setModal(null)}>Cancelar</Btn>
            <Btn onClick={handleSave} disabled={saving}>
              {saving
                ? <><i className="fa-solid fa-circle-notch fa-spin" />Guardando…</>
                : <><i className="fa-solid fa-floppy-disk" />Guardar</>}
            </Btn>
          </div>
        </Modal>
      )}

      {/* Confirmar eliminar condominio */}
      {confirmDel && (
        <Modal title="Eliminar condominio" onClose={() => setConfirmDel(null)}>
          <div style={{ textAlign: "center", padding: "8px 0 20px" }}>
            <div style={{ fontSize: "2.4rem", marginBottom: 10 }}>🗑️</div>
            <p style={{ color: C.text, margin: "0 0 8px", fontWeight: 700 }}>
              ¿Eliminar "{confirmDel.nombre}"?
            </p>
            <p style={{ color: "#f87171", fontSize: "0.83rem", lineHeight: 1.6, margin: 0 }}>
              Esta acción es <strong>permanente e irreversible</strong>. Se eliminarán todos los
              datos del condominio (filiales, residentes, facturas, asientos, etc.).
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <Btn variant="ghost" onClick={() => setConfirmDel(null)}>Cancelar</Btn>
            <Btn variant="danger" onClick={() => handleDelete(confirmDel)}>
              <i className="fa-solid fa-trash" />Sí, eliminar permanentemente
            </Btn>
          </div>
        </Modal>
      )}

      {/* Confirmar reset total del condominio */}
      {confirmReset && (
        <Modal title="Reiniciar condominio desde cero" onClose={() => { setConfirmReset(null); setResetText(""); }}>
          <div style={{ padding: "4px 0 14px" }}>
            <div style={{ textAlign: "center", fontSize: "2.2rem", marginBottom: 10 }}>⚠️</div>
            <p style={{ color: C.text, margin: "0 0 8px", fontWeight: 700, textAlign: "center" }}>
              ¿Reiniciar "{confirmReset.nombre}" en su totalidad?
            </p>
            <p style={{ color: "#f87171", fontSize: "0.83rem", lineHeight: 1.6, margin: "0 0 14px" }}>
              Se borrarán <strong>TODOS los datos</strong>: filiales, residentes, facturas, recibos,
              asientos, cuentas bancarias, proveedores, catálogo de cuentas, áreas comunes y avisos.
              Los consecutivos vuelven a 0 y el catálogo base se regenera.
              Los <strong>usuarios NO se eliminan</strong>. Esta acción es <strong>irreversible</strong>.
            </p>
            <p style={{ color: C.muted, fontSize: "0.8rem", margin: "0 0 6px" }}>
              Para confirmar, escribí el nombre exacto del condominio:
            </p>
            <input
              value={resetText}
              onChange={e => setResetText(e.target.value)}
              placeholder={confirmReset.nombre}
              style={{
                width: "100%", padding: "9px 12px", borderRadius: 7,
                background: C.bg1, border: `1px solid ${resetText === confirmReset.nombre ? "rgba(74,222,128,0.5)" : C.border}`,
                color: C.text, fontSize: "0.88rem", outline: "none", boxSizing: "border-box",
              }}
            />
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <Btn variant="ghost" onClick={() => { setConfirmReset(null); setResetText(""); }}>Cancelar</Btn>
            <Btn variant="danger" disabled={resetText !== confirmReset.nombre || resetting} onClick={handleReset}>
              {resetting
                ? <><i className="fa-solid fa-circle-notch fa-spin" />Reiniciando…</>
                : <><i className="fa-solid fa-rotate-left" />Sí, reiniciar desde cero</>}
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Usuarios Tab ──────────────────────────────────────────────────────────────
function UsuariosTab() {
  const [usuarios, setUsuarios]   = useState([]);
  const [condominios, setCond]    = useState([]);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(null);
  const [form, setForm]           = useState({});
  const [errors, setErrors]       = useState({});
  const [saving, setSaving]       = useState(false);
  const [toast, setToast]         = useState({ msg: "", type: "" });
  const [showPw, setShowPw]       = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting]   = useState(null);
  const [loadErr, setLoadErr]     = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setLoadErr("");
    Promise.all([
      apiFetch("/api/core/admin/usuarios/").then(r => readJsonOrThrow(r, "Usuarios")),
      apiFetch("/api/core/admin/condominios/").then(r => readJsonOrThrow(r, "Condominios")),
    ]).then(([u, c]) => {
      setUsuarios(Array.isArray(u) ? u : []);
      setCond(Array.isArray(c) ? c : []);
    }).catch(e => setLoadErr(e.message || "Error al cargar usuarios"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: "", type: "" }), 5000);
  };

  function openCreate() {
    setForm({ username: "", password: "", email: "", first_name: "", last_name: "", condominio_id: "", is_superuser: false });
    setErrors({});
    setModal("create");
    setShowPw(false);
  }

  function openEdit(u) {
    setForm({ ...u, password: "", condominio_id: u.condominio_id ?? "" });
    setErrors({});
    setModal(u);
    setShowPw(false);
  }

  async function handleSave() {
    const errs = {};
    if (!form.username?.trim()) errs.username = "Requerido";
    if (modal === "create" && !form.password?.trim()) errs.password = "Requerido";
    if (modal === "create" && form.password && form.password.length < 8) errs.password = "Mínimo 8 caracteres";
    if (!form.is_superuser && !form.condominio_id) errs.condominio_id = "Requerido para usuarios normales";
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSaving(true);
    try {
      const isEdit = modal !== "create";
      const url    = isEdit ? `/api/core/admin/usuarios/${modal.id}/` : "/api/core/admin/usuarios/";
      const method = isEdit ? "PATCH" : "POST";
      const body   = { ...form };
      if (isEdit && !body.password) delete body.password;
      const res = await apiFetch(url, { method, body: JSON.stringify(body) });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        setErrors(typeof e === "object" ? e : { general: JSON.stringify(e) });
        return;
      }
      setModal(null);
      load();
      showToast(isEdit ? "Usuario actualizado ✓" : "Usuario creado ✓");
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(u) {
    setDeleting(u.id);
    setConfirmDel(null);
    try {
      const res = await apiFetch(`/api/core/admin/usuarios/${u.id}/`, { method: "DELETE" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        showToast(err.detail || "Error al eliminar usuario", "error");
        return;
      }
      load();
      showToast(`Usuario "${u.username}" eliminado`);
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setDeleting(null);
    }
  }

  async function handleToggleActive(u) {
    try {
      const res = await apiFetch(`/api/core/admin/usuarios/${u.id}/`, {
        method: "PATCH",
        body: JSON.stringify({ is_active: !u.is_active }),
      });
      if (!res.ok) { showToast("Error al cambiar estado", "error"); return; }
      load();
      showToast(u.is_active ? `"${u.username}" desactivado` : `"${u.username}" activado`);
    } catch (e) { showToast(e.message, "error"); }
  }

  const f = key => ({ value: form[key] ?? "", onChange: e => setForm(p => ({ ...p, [key]: e.target.value })) });

  return (
    <div>
      <Toast msg={toast.msg} type={toast.type} onClose={() => setToast({ msg: "", type: "" })} />
      <ErrorBanner msg={loadErr} onRetry={load} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <span style={{ color: C.muted, fontSize: "0.85rem" }}>{usuarios.length} usuarios registrados</span>
        <Btn onClick={openCreate}><i className="fa-solid fa-user-plus" />Nuevo usuario</Btn>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: C.muted }}>
          <i className="fa-solid fa-circle-notch fa-spin" style={{ color: C.accent, marginRight: 8 }} />Cargando…
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {usuarios.map(u => (
            <div key={u.id} style={{
              background: C.bg1, border: `1px solid ${C.border}`,
              borderRadius: 10, padding: "14px 18px",
              display: "flex", alignItems: "center", gap: 14,
              opacity: u.is_active ? 1 : 0.6,
            }}>
              <div style={{
                width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
                background: u.is_superuser ? "rgba(96,165,250,0.15)" : "rgba(249,115,22,0.1)",
                border: `1px solid ${u.is_superuser ? "rgba(96,165,250,0.3)" : "rgba(249,115,22,0.2)"}`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <i className={`fa-solid fa-${u.is_superuser ? "shield-halved" : "user"}`}
                  style={{ color: u.is_superuser ? "#60a5fa" : C.accent }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 700, color: C.text, fontSize: "0.92rem" }}>{u.username}</span>
                  {(u.first_name || u.last_name) && (
                    <span style={{ color: C.muted, fontSize: "0.8rem" }}>
                      ({`${u.first_name || ""} ${u.last_name || ""}`.trim()})
                    </span>
                  )}
                  {u.is_superuser && (
                    <span style={{ fontSize: "0.65rem", padding: "1px 6px", borderRadius: 4, background: "rgba(96,165,250,0.15)", color: "#60a5fa", fontWeight: 700 }}>ADMIN</span>
                  )}
                  {!u.is_active && (
                    <span style={{ fontSize: "0.65rem", padding: "1px 6px", borderRadius: 4, background: "rgba(239,68,68,0.1)", color: "#f87171", fontWeight: 700 }}>INACTIVO</span>
                  )}
                </div>
                <div style={{ color: C.muted, fontSize: "0.77rem", marginTop: 2 }}>
                  {u.email || "Sin email"}
                  {u.condominio_nombre && <> · <span style={{ color: "#94a3b8" }}>{u.condominio_nombre}</span></>}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <Btn small variant="ghost" onClick={() => handleToggleActive(u)}>
                  <i className={`fa-solid fa-${u.is_active ? "toggle-on" : "toggle-off"}`} />
                  {u.is_active ? "Activo" : "Inactivo"}
                </Btn>
                <Btn small variant="ghost" onClick={() => openEdit(u)}>
                  <i className="fa-solid fa-pencil" />Editar
                </Btn>
                <Btn small variant="danger" disabled={deleting === u.id} onClick={() => setConfirmDel(u)}>
                  {deleting === u.id
                    ? <i className="fa-solid fa-circle-notch fa-spin" />
                    : <i className="fa-solid fa-trash" />}
                </Btn>
              </div>
            </div>
          ))}
          {usuarios.length === 0 && (
            <div style={{ textAlign: "center", padding: 40, color: C.muted }}>
              <i className="fa-solid fa-users" style={{ fontSize: "2rem", display: "block", marginBottom: 10, opacity: 0.2 }} />
              No hay usuarios registrados.
            </div>
          )}
        </div>
      )}

      {/* Modal crear/editar */}
      {modal && (
        <Modal title={modal === "create" ? "Nuevo Usuario" : `Editar — ${modal.username}`} onClose={() => setModal(null)}>
          {errors.general && (
            <div style={{ color: "#f87171", marginBottom: 12, fontSize: "0.84rem", padding: "8px 12px", background: "rgba(239,68,68,0.08)", borderRadius: 6 }}>
              {errors.general}
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            <Input label="Nombre" {...f("first_name")} placeholder="María" />
            <Input label="Apellidos" {...f("last_name")} placeholder="González" />
          </div>
          <Input label="Username" required {...f("username")} error={errors.username}
            placeholder="usuario123" disabled={modal !== "create"} />
          <Input
            label={modal === "create" ? "Contraseña" : "Nueva contraseña (vacío = no cambia)"}
            required={modal === "create"} type={showPw ? "text" : "password"}
            {...f("password")} error={errors.password}
            placeholder={modal === "create" ? "Mínimo 8 caracteres" : "Dejar vacío para no cambiar"} />
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: "0.8rem", color: C.muted }}>
              <input type="checkbox" checked={showPw} onChange={e => setShowPw(e.target.checked)} />
              Mostrar contraseña
            </label>
          </div>
          <Input label="Email" type="email" {...f("email")} error={errors.email} placeholder="usuario@email.com" />
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: "0.8rem", color: C.muted }}>
              <input type="checkbox"
                checked={!!form.is_superuser}
                onChange={e => setForm(p => ({ ...p, is_superuser: e.target.checked, condominio_id: e.target.checked ? "" : p.condominio_id }))} />
              <span style={{ fontWeight: 600, color: form.is_superuser ? "#60a5fa" : C.muted }}>
                Superadministrador (acceso a todos los condominios)
              </span>
            </label>
          </div>
          {!form.is_superuser && (
            <Select label="Condominio asignado" required error={errors.condominio_id}
              value={form.condominio_id ?? ""}
              onChange={e => setForm(p => ({ ...p, condominio_id: e.target.value }))}>
              <option value="">— Seleccionar condominio —</option>
              {condominios.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </Select>
          )}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
            <Btn variant="ghost" onClick={() => setModal(null)}>Cancelar</Btn>
            <Btn onClick={handleSave} disabled={saving}>
              {saving
                ? <><i className="fa-solid fa-circle-notch fa-spin" />Guardando…</>
                : <><i className="fa-solid fa-floppy-disk" />Guardar</>}
            </Btn>
          </div>
        </Modal>
      )}

      {/* Confirmar eliminar usuario */}
      {confirmDel && (
        <Modal title="Eliminar usuario" onClose={() => setConfirmDel(null)}>
          <div style={{ textAlign: "center", padding: "8px 0 20px" }}>
            <div style={{ fontSize: "2.4rem", marginBottom: 10 }}>🗑️</div>
            <p style={{ color: C.text, margin: "0 0 8px", fontWeight: 700 }}>
              ¿Eliminar al usuario "{confirmDel.username}"?
            </p>
            <p style={{ color: C.muted, fontSize: "0.83rem", lineHeight: 1.6, margin: 0 }}>
              Esta acción no se puede deshacer.
              {confirmDel.condominio_nombre && ` El usuario está asociado al condominio "${confirmDel.condominio_nombre}".`}
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <Btn variant="ghost" onClick={() => setConfirmDel(null)}>Cancelar</Btn>
            <Btn variant="danger" onClick={() => handleDelete(confirmDel)}>
              <i className="fa-solid fa-trash" />Sí, eliminar
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Contadores Tab ────────────────────────────────────────────────────────────
function ContadoresTab() {
  const [contadores, setContadores]   = useState([]);
  const [condominios, setCond]        = useState([]);
  const [loading, setLoading]         = useState(true);
  const [modal, setModal]             = useState(null);   // null | "create" | user obj
  const [assignModal, setAssignModal] = useState(null);   // null | user obj
  const [form, setForm]               = useState({});
  const [errors, setErrors]           = useState({});
  const [saving, setSaving]           = useState(false);
  const [toast, setToast]             = useState({ msg: "", type: "" });
  const [showPw, setShowPw]           = useState(false);
  const [selectedCondos, setSelectedCondos] = useState(new Set());
  const [assigning, setAssigning]     = useState(false);
  const [confirmDel, setConfirmDel]   = useState(null);
  const [deleting, setDeleting]       = useState(null);
  const [loadErr, setLoadErr]         = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setLoadErr("");
    Promise.all([
      apiFetch("/api/core/admin/contadores/").then(r => readJsonOrThrow(r, "Contadores")),
      apiFetch("/api/core/admin/condominios/").then(r => readJsonOrThrow(r, "Condominios")),
    ]).then(([c, conds]) => {
      setContadores(Array.isArray(c) ? c : []);
      setCond(Array.isArray(conds) ? conds : []);
    }).catch(e => setLoadErr(e.message || "Error al cargar contadores"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: "", type: "" }), 5000);
  };

  function openCreate() {
    setForm({ username: "", password: "", email: "", first_name: "", last_name: "" });
    setErrors({});
    setModal("create");
    setShowPw(false);
  }

  function openEdit(u) {
    setForm({ ...u, password: "" });
    setErrors({});
    setModal(u);
    setShowPw(false);
  }

  function openAssign(u) {
    setAssignModal(u);
    setSelectedCondos(new Set(u.condominios.map(c => c.id)));
  }

  async function handleSave() {
    const errs = {};
    if (!form.username?.trim()) errs.username = "Requerido";
    if (modal === "create" && !form.password?.trim()) errs.password = "Requerido";
    if (modal === "create" && form.password && form.password.length < 8) errs.password = "Mínimo 8 caracteres";
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSaving(true);
    try {
      const isEdit = modal !== "create";
      const url    = isEdit ? `/api/core/admin/contadores/${modal.id}/` : "/api/core/admin/contadores/";
      const method = isEdit ? "PATCH" : "POST";
      const body   = { ...form };
      if (isEdit && !body.password) delete body.password;
      const res = await apiFetch(url, { method, body: JSON.stringify(body) });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        setErrors(typeof e === "object" ? e : { general: JSON.stringify(e) });
        return;
      }
      setModal(null);
      load();
      showToast(isEdit ? "Contador actualizado ✓" : "Contador creado ✓");
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleAssign() {
    if (!assignModal) return;
    setAssigning(true);
    try {
      const res = await apiFetch(`/api/core/admin/contadores/${assignModal.id}/condominios/`, {
        method: "PUT",
        body: JSON.stringify({ condominio_ids: [...selectedCondos] }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        showToast(e.detail || e.condominio_ids || "Error al asignar", "error");
        return;
      }
      setAssignModal(null);
      load();
      showToast("Condominios asignados ✓");
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setAssigning(false);
    }
  }

  async function handleDelete(u) {
    setDeleting(u.id);
    setConfirmDel(null);
    try {
      const res = await apiFetch(`/api/core/admin/contadores/${u.id}/`, { method: "DELETE" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        showToast(err.detail || "Error al eliminar", "error");
        return;
      }
      load();
      showToast(`Contador "${u.username}" eliminado`);
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setDeleting(null);
    }
  }

  async function handleToggleActive(u) {
    try {
      const res = await apiFetch(`/api/core/admin/contadores/${u.id}/`, {
        method: "PATCH",
        body: JSON.stringify({ is_active: !u.is_active }),
      });
      if (!res.ok) { showToast("Error al cambiar estado", "error"); return; }
      load();
      showToast(u.is_active ? `"${u.username}" desactivado` : `"${u.username}" activado`);
    } catch (e) { showToast(e.message, "error"); }
  }

  const f = key => ({ value: form[key] ?? "", onChange: e => setForm(p => ({ ...p, [key]: e.target.value })) });

  const toggleCondo = (id) => setSelectedCondos(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  return (
    <div>
      <Toast msg={toast.msg} type={toast.type} onClose={() => setToast({ msg: "", type: "" })} />
      <ErrorBanner msg={loadErr} onRetry={load} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <span style={{ color: C.muted, fontSize: "0.85rem" }}>{contadores.length} contadores registrados</span>
        <Btn onClick={openCreate}><i className="fa-solid fa-user-plus" />Nuevo contador</Btn>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: C.muted }}>
          <i className="fa-solid fa-circle-notch fa-spin" style={{ color: C.accent, marginRight: 8 }} />Cargando…
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {contadores.map(u => (
            <div key={u.id} style={{
              background: C.bg1, border: `1px solid ${C.border}`,
              borderRadius: 10, padding: "14px 18px",
              display: "flex", alignItems: "center", gap: 14,
              opacity: u.is_active ? 1 : 0.6,
            }}>
              <div style={{
                width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
                background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.3)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <i className="fa-solid fa-calculator" style={{ color: "#10b981" }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 700, color: C.text, fontSize: "0.92rem" }}>{u.username}</span>
                  {(u.first_name || u.last_name) && (
                    <span style={{ color: C.muted, fontSize: "0.8rem" }}>
                      ({`${u.first_name || ""} ${u.last_name || ""}`.trim()})
                    </span>
                  )}
                  <span style={{ fontSize: "0.65rem", padding: "1px 6px", borderRadius: 4, background: "rgba(16,185,129,0.12)", color: "#10b981", fontWeight: 700 }}>CONTADOR</span>
                  {!u.is_active && (
                    <span style={{ fontSize: "0.65rem", padding: "1px 6px", borderRadius: 4, background: "rgba(239,68,68,0.1)", color: "#f87171", fontWeight: 700 }}>INACTIVO</span>
                  )}
                </div>
                <div style={{ color: C.muted, fontSize: "0.77rem", marginTop: 3, display: "flex", flexWrap: "wrap", gap: "4px 10px" }}>
                  <span>{u.email || "Sin email"}</span>
                  {u.condominios.length > 0 ? (
                    <span style={{ color: "#94a3b8" }}>
                      {u.condominios.map(c => c.nombre).join(" · ")}
                    </span>
                  ) : (
                    <span style={{ color: "#f87171" }}>Sin condominios asignados</span>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0, flexWrap: "wrap" }}>
                <Btn small variant="ghost" onClick={() => handleToggleActive(u)}>
                  <i className={`fa-solid fa-${u.is_active ? "toggle-on" : "toggle-off"}`} />
                  {u.is_active ? "Activo" : "Inactivo"}
                </Btn>
                <Btn small variant="success" onClick={() => openAssign(u)}>
                  <i className="fa-solid fa-building" />Condominios
                </Btn>
                <Btn small variant="ghost" onClick={() => openEdit(u)}>
                  <i className="fa-solid fa-pencil" />Editar
                </Btn>
                <Btn small variant="danger" disabled={deleting === u.id} onClick={() => setConfirmDel(u)}>
                  {deleting === u.id
                    ? <i className="fa-solid fa-circle-notch fa-spin" />
                    : <i className="fa-solid fa-trash" />}
                </Btn>
              </div>
            </div>
          ))}
          {contadores.length === 0 && (
            <div style={{ textAlign: "center", padding: 40, color: C.muted }}>
              <i className="fa-solid fa-calculator" style={{ fontSize: "2rem", display: "block", marginBottom: 10, opacity: 0.2 }} />
              No hay contadores registrados.
            </div>
          )}
        </div>
      )}

      {/* Modal crear/editar */}
      {modal && (
        <Modal title={modal === "create" ? "Nuevo Contador" : `Editar — ${modal.username}`} onClose={() => setModal(null)}>
          {errors.general && (
            <div style={{ color: "#f87171", marginBottom: 12, fontSize: "0.84rem", padding: "8px 12px", background: "rgba(239,68,68,0.08)", borderRadius: 6 }}>
              {errors.general}
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            <Input label="Nombre" {...f("first_name")} placeholder="Juan" />
            <Input label="Apellidos" {...f("last_name")} placeholder="Pérez" />
          </div>
          <Input label="Username" required {...f("username")} error={errors.username}
            placeholder="contador123" disabled={modal !== "create"} />
          <Input
            label={modal === "create" ? "Contraseña" : "Nueva contraseña (vacío = no cambia)"}
            required={modal === "create"} type={showPw ? "text" : "password"}
            {...f("password")} error={errors.password}
            placeholder={modal === "create" ? "Mínimo 8 caracteres" : "Dejar vacío para no cambiar"} />
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: "0.8rem", color: C.muted }}>
              <input type="checkbox" checked={showPw} onChange={e => setShowPw(e.target.checked)} />
              Mostrar contraseña
            </label>
          </div>
          <Input label="Email" type="email" {...f("email")} error={errors.email} placeholder="contador@email.com" />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
            <Btn variant="ghost" onClick={() => setModal(null)}>Cancelar</Btn>
            <Btn onClick={handleSave} disabled={saving}>
              {saving
                ? <><i className="fa-solid fa-circle-notch fa-spin" />Guardando…</>
                : <><i className="fa-solid fa-floppy-disk" />Guardar</>}
            </Btn>
          </div>
        </Modal>
      )}

      {/* Modal asignar condominios */}
      {assignModal && (
        <Modal title={`Condominios — ${assignModal.username}`} onClose={() => setAssignModal(null)}>
          <p style={{ color: C.muted, fontSize: "0.84rem", marginTop: 0 }}>
            Selecciona los condominios a los que este contador tendrá acceso:
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 320, overflowY: "auto", marginBottom: 16 }}>
            {condominios.length === 0 && (
              <div style={{ color: C.muted, textAlign: "center", padding: 20 }}>No hay condominios disponibles.</div>
            )}
            {condominios.map(c => (
              <label key={c.id} style={{
                display: "flex", alignItems: "center", gap: 12, cursor: "pointer",
                background: selectedCondos.has(c.id) ? "rgba(249,115,22,0.07)" : C.bg1,
                border: `1px solid ${selectedCondos.has(c.id) ? "rgba(249,115,22,0.3)" : C.border}`,
                borderRadius: 8, padding: "10px 14px", transition: "all 0.1s",
              }}>
                <input type="checkbox"
                  checked={selectedCondos.has(c.id)}
                  onChange={() => toggleCondo(c.id)} />
                <span style={{ fontWeight: 600, color: C.text, fontSize: "0.88rem" }}>{c.nombre}</span>
                {c.multimoneda_activa && (
                  <span style={{ marginLeft: "auto", fontSize: "0.7rem", background: "rgba(96,165,250,0.12)", color: "#60a5fa", border: "1px solid rgba(96,165,250,0.3)", borderRadius: 4, padding: "1px 7px", fontWeight: 600 }}>
                    USD
                  </span>
                )}
              </label>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ color: C.muted, fontSize: "0.82rem" }}>{selectedCondos.size} seleccionado(s)</span>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn variant="ghost" onClick={() => setAssignModal(null)}>Cancelar</Btn>
              <Btn onClick={handleAssign} disabled={assigning}>
                {assigning
                  ? <><i className="fa-solid fa-circle-notch fa-spin" />Guardando…</>
                  : <><i className="fa-solid fa-floppy-disk" />Guardar accesos</>}
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* Confirmar eliminar */}
      {confirmDel && (
        <Modal title="Eliminar contador" onClose={() => setConfirmDel(null)}>
          <div style={{ textAlign: "center", padding: "8px 0 20px" }}>
            <div style={{ fontSize: "2.4rem", marginBottom: 10 }}>🗑️</div>
            <p style={{ color: C.text, margin: "0 0 8px", fontWeight: 700 }}>
              ¿Eliminar al contador "{confirmDel.username}"?
            </p>
            <p style={{ color: C.muted, fontSize: "0.83rem", lineHeight: 1.6, margin: 0 }}>
              Esta acción no se puede deshacer.
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <Btn variant="ghost" onClick={() => setConfirmDel(null)}>Cancelar</Btn>
            <Btn variant="danger" onClick={() => handleDelete(confirmDel)}>
              <i className="fa-solid fa-trash" />Sí, eliminar
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Documentos Tab ────────────────────────────────────────────────────────────
const TIPOS_DOC = [
  // Residentes
  {
    value: "filiales", label: "Filiales", group: "Residentes",
    icon: "fa-home", color: "#a78bfa",
    cols: ["ID Filial", "Tipo Propiedad", "Núm. Plano"],
    warning: "⚠️ Eliminar filiales borrará en cascada sus propietarios, copropietarios, autorizados, facturas, recibos y CxC asociadas.",
  },
  {
    value: "propietarios", label: "Propietarios", group: "Residentes",
    icon: "fa-user-tie", color: "#60a5fa",
    cols: ["Filial", "Cédula", "Nombre completo"],
  },
  {
    value: "copropietarios", label: "Copropietarios", group: "Residentes",
    icon: "fa-user-group", color: "#60a5fa",
    cols: ["Filial", "Cédula", "Nombre completo"],
  },
  {
    value: "autorizados", label: "Autorizados", group: "Residentes",
    icon: "fa-id-card", color: "#60a5fa",
    cols: ["Filial", "Cédula", "Nombre completo", "Tipo"],
  },
  // Contabilidad — Asientos
  {
    value: "asientos", label: "Asientos de Diario", group: "Contabilidad",
    icon: "fa-book", color: "#fbbf24",
    cols: ["Número", "Fecha", "Concepto", "Tipo"],
  },
  {
    value: "ingresos", label: "Registro de Ingresos", group: "Contabilidad",
    icon: "fa-arrow-trend-up", color: "#4ade80",
    cols: ["Número", "Fecha", "Concepto"],
  },
  {
    value: "gastos", label: "Registro de Gastos", group: "Contabilidad",
    icon: "fa-receipt", color: "#f87171",
    cols: ["Número", "Fecha", "Concepto"],
  },
  // CxC
  {
    value: "facturas", label: "Facturas CxC", group: "Cuentas x Cobrar",
    icon: "fa-file-invoice-dollar", color: "#f97316",
    cols: ["Número", "Fecha", "Filial", "Concepto", "Monto"],
  },
  {
    value: "recibos", label: "Recibos CxC", group: "Cuentas x Cobrar",
    icon: "fa-money-check-dollar", color: "#f97316",
    cols: ["Número", "Fecha", "Filial", "Monto"],
  },
  // CxP
  {
    value: "facturas_cxp", label: "Facturas CxP", group: "Cuentas x Pagar",
    icon: "fa-file-invoice", color: "#fb923c",
    cols: ["Número", "Fecha", "Concepto", "Monto"],
  },
  {
    value: "recibos_cxp", label: "Recibos CxP", group: "Cuentas x Pagar",
    icon: "fa-money-check", color: "#fb923c",
    cols: ["Número", "Fecha", "Monto"],
  },
];

const GRUPOS = ["Residentes", "Contabilidad", "Cuentas x Cobrar", "Cuentas x Pagar"];

function DocumentosTab() {
  const [condominios, setCond]     = useState([]);
  const [condoId, setCondoId]      = useState("");
  const [tipo, setTipo]            = useState("filiales");
  const [search, setSearch]        = useState("");
  const [docs, setDocs]            = useState([]);
  const [total, setTotal]          = useState(0);
  const [page, setPage]            = useState(1);
  const [pages, setPages]          = useState(1);
  const [loading, setLoading]      = useState(false);
  const [selected, setSelected]    = useState(new Set());
  const [deleting, setDeleting]    = useState(false);
  const [toast, setToast]          = useState({ msg: "", type: "" });
  const [loadErr, setLoadErr]      = useState("");

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: "", type: "" }), 5000);
  };

  useEffect(() => {
    apiFetch("/api/core/admin/condominios/")
      .then(r => readJsonOrThrow(r, "Condominios"))
      .then(d => setCond(Array.isArray(d) ? d : []))
      .catch(e => showToast(e.message, "error"));
  }, []);

  const loadDocs = useCallback((p = 1) => {
    if (!condoId) return;
    setLoading(true);
    setLoadErr("");
    setSelected(new Set());
    apiFetch(`/api/core/admin/documentos/?condominio_id=${condoId}&tipo=${tipo}&search=${encodeURIComponent(search)}&page=${p}`)
      .then(r => readJsonOrThrow(r, "Registros"))
      .then(d => {
        setDocs(d.results || []);
        setTotal(d.total || 0);
        setPage(d.page || 1);
        setPages(d.pages || 1);
      })
      .catch(e => { setDocs([]); setLoadErr(e.message || "Error al cargar registros"); })
      .finally(() => setLoading(false));
  }, [condoId, tipo, search]);

  useEffect(() => { if (condoId) loadDocs(1); }, [condoId, tipo]);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === docs.length) setSelected(new Set());
    else setSelected(new Set(docs.map(d => d.id)));
  };

  async function handleDelete() {
    if (!selected.size) { showToast("Seleccione al menos un registro", "error"); return; }
    const tipoInfo  = TIPOS_DOC.find(t => t.value === tipo);
    const tipoLabel = tipoInfo?.label || tipo;
    const warningExtra = tipoInfo?.warning ? `\n\n${tipoInfo.warning}` : "";

    if (!window.confirm(
      `¿Eliminar ${selected.size} ${tipoLabel}?\n\nEsta acción es permanente e irreversible.${warningExtra}`
    )) return;

    setDeleting(true);
    try {
      const res  = await apiFetch("/api/core/admin/eliminar-rango/", {
        method: "POST",
        body: JSON.stringify({ condominio_id: condoId, tipo, ids: [...selected] }),
      });
      const data = await res.json();
      if (!res.ok) { showToast(data.error || "Error al eliminar", "error"); return; }
      const errMsg = data.errores?.length ? ` (${data.errores.length} con error)` : "";
      showToast(`${data.eliminados} registro(s) eliminado(s)${errMsg}`);
      setSelected(new Set());
      loadDocs(1);
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setDeleting(false);
    }
  }

  const tipoInfo = TIPOS_DOC.find(t => t.value === tipo);
  const fmtMonto = (n) => n != null ? `₡${Number(n).toLocaleString("es-CR")}` : "";

  const cellVal = (doc) => {
    if (tipo === "filiales")       return [doc.numero, doc.concepto, doc.extra || "—"];
    if (tipo === "propietarios")   return [doc.numero, doc.concepto, doc.extra];
    if (tipo === "copropietarios") return [doc.numero, doc.concepto, doc.extra];
    if (tipo === "autorizados")    return [doc.numero, doc.concepto, doc.extra, doc.tipo || "—"];
    if (tipo === "asientos")       return [doc.numero, doc.fecha, doc.concepto, doc.tipo];
    if (tipo === "ingresos")       return [doc.numero, doc.fecha, doc.concepto];
    if (tipo === "gastos")         return [doc.numero, doc.fecha, doc.concepto];
    if (tipo === "facturas")       return [doc.numero, doc.fecha, doc.filial, doc.concepto, fmtMonto(doc.monto)];
    if (tipo === "recibos")        return [doc.numero, doc.fecha, doc.filial, fmtMonto(doc.monto)];
    if (tipo === "facturas_cxp")   return [doc.numero, doc.fecha, doc.concepto, fmtMonto(doc.monto)];
    if (tipo === "recibos_cxp")    return [doc.numero, doc.fecha, fmtMonto(doc.monto)];
    return [];
  };

  const inpSt = {
    background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 8,
    color: C.text, padding: "9px 14px", fontSize: "0.85rem", outline: "none",
  };

  return (
    <div>
      <Toast msg={toast.msg} type={toast.type} onClose={() => setToast({ msg: "", type: "" })} />
      <ErrorBanner msg={loadErr} onRetry={condoId ? () => loadDocs(1) : null} />

      {/* Aviso */}
      <div style={{
        background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.2)",
        borderRadius: 10, padding: "12px 16px", marginBottom: 20, fontSize: "0.82rem",
        color: "#fca5a5", display: "flex", alignItems: "flex-start", gap: 10,
      }}>
        <i className="fa-solid fa-triangle-exclamation" style={{ flexShrink: 0, marginTop: 2 }} />
        <span>
          Esta función elimina registros de forma <strong>permanente e irreversible</strong>.
          Úsela solo cuando el condominio lo solicite explícitamente.
          Los registros relacionados (CxC, asientos, movimientos bancarios) también se eliminan.
        </span>
      </div>

      {/* Filtros */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 12, marginBottom: 20, alignItems: "end" }}>
        <div>
          <label style={{ fontSize: "0.7rem", fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 5 }}>
            Condominio
          </label>
          <select value={condoId} onChange={e => { setCondoId(e.target.value); setDocs([]); setSelected(new Set()); }}
            style={{ ...inpSt, width: "100%" }}>
            <option value="">— Seleccionar —</option>
            {condominios.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
        </div>

        <div>
          <label style={{ fontSize: "0.7rem", fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 5 }}>
            Tipo de registro
          </label>
          <select value={tipo} onChange={e => { setTipo(e.target.value); setDocs([]); setSelected(new Set()); }}
            style={{ ...inpSt, width: "100%" }}>
            {GRUPOS.map(g => (
              <optgroup key={g} label={g}>
                {TIPOS_DOC.filter(t => t.group === g).map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        <div>
          <label style={{ fontSize: "0.7rem", fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 5 }}>
            Buscar
          </label>
          <input value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === "Enter" && loadDocs(1)}
            placeholder="N° documento, nombre, cédula…"
            style={{ ...inpSt, width: "100%", boxSizing: "border-box" }} />
        </div>

        <Btn onClick={() => loadDocs(1)} disabled={!condoId || loading}>
          <i className={`fa-solid fa-${loading ? "circle-notch fa-spin" : "magnifying-glass"}`} />
          Buscar
        </Btn>
      </div>

      {/* Advertencia específica del tipo */}
      {tipoInfo?.warning && (
        <div style={{
          background: "rgba(251,191,36,0.06)", border: "1px solid rgba(251,191,36,0.25)",
          borderRadius: 8, padding: "10px 14px", marginBottom: 16,
          fontSize: "0.8rem", color: "#fde68a", display: "flex", gap: 8, alignItems: "flex-start",
        }}>
          <i className="fa-solid fa-triangle-exclamation" style={{ flexShrink: 0, marginTop: 2 }} />
          <span>{tipoInfo.warning}</span>
        </div>
      )}

      {/* Tabla */}
      {docs.length > 0 ? (
        <>
          {/* Barra de acciones */}
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            marginBottom: 12, padding: "10px 14px",
            background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 8,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: "0.84rem", color: C.muted }}>
                <input type="checkbox"
                  checked={selected.size === docs.length && docs.length > 0}
                  onChange={toggleAll} />
                Todos ({docs.length} en página)
              </label>
              {selected.size > 0 && (
                <span style={{ color: C.accent, fontSize: "0.84rem", fontWeight: 600 }}>
                  {selected.size} seleccionados
                </span>
              )}
              <span style={{ color: C.muted, fontSize: "0.78rem" }}>
                Total: {total} registros
              </span>
            </div>
            <Btn variant="danger" onClick={handleDelete} disabled={deleting || selected.size === 0}>
              {deleting
                ? <><i className="fa-solid fa-circle-notch fa-spin" />Eliminando…</>
                : <><i className="fa-solid fa-trash" />Eliminar seleccionados ({selected.size})</>}
            </Btn>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.83rem" }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${C.border}` }}>
                  <th style={{ width: 36, padding: "8px 12px" }} />
                  {tipoInfo?.cols.map(col => (
                    <th key={col} style={{
                      padding: "8px 12px", textAlign: "left",
                      color: C.muted, fontWeight: 700, fontSize: "0.7rem",
                      textTransform: "uppercase", letterSpacing: "0.5px",
                    }}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {docs.map((doc, i) => (
                  <tr key={doc.id}
                    style={{
                      background: selected.has(doc.id)
                        ? "rgba(249,115,22,0.06)"
                        : i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.02)",
                      borderBottom: `1px solid ${C.border}`, cursor: "pointer",
                    }}
                    onClick={() => toggleSelect(doc.id)}>
                    <td style={{ padding: "8px 12px", textAlign: "center" }}>
                      <input type="checkbox" checked={selected.has(doc.id)}
                        onChange={() => toggleSelect(doc.id)}
                        onClick={e => e.stopPropagation()} />
                    </td>
                    {cellVal(doc).map((v, ci) => (
                      <td key={ci} style={{
                        padding: "8px 12px",
                        color: ci === 0 ? tipoInfo?.color || C.accent : C.text,
                        fontFamily: ci === 0 ? "monospace" : "inherit",
                        maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>
                        {v || "—"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {pages > 1 && (
            <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 16, flexWrap: "wrap" }}>
              <button onClick={() => loadDocs(page - 1)} disabled={page <= 1}
                style={{ padding: "5px 10px", borderRadius: 6, fontSize: "0.82rem", background: "transparent", color: C.muted, border: `1px solid ${C.border}`, cursor: "pointer", opacity: page <= 1 ? 0.4 : 1 }}>
                ‹
              </button>
              {Array.from({ length: pages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === pages || Math.abs(p - page) <= 2)
                .map((p, idx, arr) => (
                  <>
                    {idx > 0 && arr[idx - 1] !== p - 1 && (
                      <span key={`ellipsis-${p}`} style={{ padding: "5px 4px", color: C.muted, fontSize: "0.82rem" }}>…</span>
                    )}
                    <button key={p} onClick={() => loadDocs(p)}
                      style={{
                        padding: "5px 12px", borderRadius: 6, fontSize: "0.82rem",
                        background: p === page ? C.accent : "transparent",
                        color: p === page ? "#fff" : C.muted,
                        border: `1px solid ${p === page ? C.accent : C.border}`,
                        cursor: "pointer",
                      }}>
                      {p}
                    </button>
                  </>
                ))}
              <button onClick={() => loadDocs(page + 1)} disabled={page >= pages}
                style={{ padding: "5px 10px", borderRadius: 6, fontSize: "0.82rem", background: "transparent", color: C.muted, border: `1px solid ${C.border}`, cursor: "pointer", opacity: page >= pages ? 0.4 : 1 }}>
                ›
              </button>
            </div>
          )}
        </>
      ) : condoId && !loading ? (
        <div style={{ textAlign: "center", padding: 40, color: C.muted }}>
          <i className="fa-solid fa-inbox" style={{ fontSize: "2rem", display: "block", marginBottom: 10, opacity: 0.2 }} />
          <p style={{ margin: 0 }}>No hay registros. Presione <strong>Buscar</strong> para cargar.</p>
        </div>
      ) : !condoId ? (
        <div style={{ textAlign: "center", padding: 40, color: C.muted }}>
          <i className="fa-solid fa-building" style={{ fontSize: "2rem", display: "block", marginBottom: 10, opacity: 0.2 }} />
          <p style={{ margin: 0 }}>Seleccione un condominio para ver sus registros.</p>
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: 40, color: C.muted }}>
          <i className="fa-solid fa-circle-notch fa-spin" style={{ color: C.accent, fontSize: "1.5rem" }} />
        </div>
      )}
    </div>
  );
}

// ── Main AdminPage ─────────────────────────────────────────────────────────────
// ── Tab Backups ───────────────────────────────────────────────────────────────
function BackupsTab() {
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [running,  setRunning]  = useState(false);
  const [msg,      setMsg]      = useState({ text: "", ok: true });

  async function cargar() {
    setLoading(true);
    try {
      const r = await apiFetch("/api/core/admin/backups/");
      setData(await r.json());
    } catch { setMsg({ text: "Error al cargar backups", ok: false }); }
    finally { setLoading(false); }
  }

  useEffect(() => { cargar(); }, []);

  async function handleBackupAhora() {
    if (!window.confirm("¿Generar un backup ahora? Puede tomar unos segundos.")) return;
    setRunning(true);
    setMsg({ text: "", ok: true });
    try {
      const r = await apiFetch("/api/core/admin/backups/", { method: "POST" });
      const json = await r.json();
      if (!r.ok) throw new Error(json.error || "Error");
      setMsg({ text: "✅ Backup generado correctamente", ok: true });
      await cargar();
    } catch(e) { setMsg({ text: e.message, ok: false }); }
    finally { setRunning(false); }
  }

  const rowSt = { padding: "11px 14px", fontSize: "0.83rem", color: C.text,
    borderBottom: `1px solid ${C.border}` };
  const thSt  = { padding: "9px 14px", fontSize: "0.7rem", fontWeight: 700,
    letterSpacing: "0.5px", textTransform: "uppercase", color: C.muted,
    borderBottom: `1px solid ${C.border}` };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, color: C.text, fontSize: "0.95rem" }}>Backups de base de datos</h3>
          <p style={{ margin: "4px 0 0", color: C.muted, fontSize: "0.78rem" }}>
            Directorio: <code style={{ color: "#60a5fa" }}>{data?.directorio || "…"}</code>
          </p>
        </div>
        <button onClick={handleBackupAhora} disabled={running}
          style={{ background: C.accent, border: "none", color: "#fff", borderRadius: 9,
            padding: "8px 18px", fontSize: "0.84rem", fontWeight: 700,
            cursor: running ? "not-allowed" : "pointer", opacity: running ? 0.7 : 1,
            display: "flex", alignItems: "center", gap: 7 }}>
          {running
            ? <><i className="fa-solid fa-circle-notch fa-spin"/>Generando…</>
            : <><i className="fa-solid fa-database"/>Backup ahora</>}
        </button>
      </div>

      {msg.text && (
        <div style={{ padding: "9px 14px", borderRadius: 8, marginBottom: 14, fontSize: "0.82rem",
          background: msg.ok ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)",
          border: `1px solid ${msg.ok ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.25)"}`,
          color: msg.ok ? "#4ade80" : "#f87171" }}>
          {msg.text}
        </div>
      )}

      {/* Instrucciones cron */}
      <div style={{ background: "rgba(96,165,250,0.06)", border: "1px solid rgba(96,165,250,0.15)",
        borderRadius: 9, padding: "12px 16px", marginBottom: 16 }}>
        <p style={{ margin: "0 0 6px", color: "#60a5fa", fontWeight: 700, fontSize: "0.8rem" }}>
          <i className="fa-solid fa-clock" style={{ marginRight: 6 }}/>Programar backup automático (cron)
        </p>
        <p style={{ margin: "0 0 6px", color: C.muted, fontSize: "0.76rem" }}>
          Agrega una de estas líneas con <code>crontab -e</code> en el servidor:
        </p>
        <code style={{ display: "block", background: "rgba(0,0,0,0.3)", borderRadius: 6,
          padding: "8px 12px", fontSize: "0.73rem", color: "#e2e8f0", marginBottom: 4,
          wordBreak: "break-all" }}>
          # Mensual (día 1 a las 3am){"\n"}
          0 3 1 * * /home/appuser/Facturacion_Staging/backend/venv/bin/python /home/appuser/Facturacion_Staging/backend/manage.py backup_db
        </code>
        <code style={{ display: "block", background: "rgba(0,0,0,0.3)", borderRadius: 6,
          padding: "8px 12px", fontSize: "0.73rem", color: "#e2e8f0", wordBreak: "break-all" }}>
          # Quincenal (días 1 y 15 a las 3am){"\n"}
          0 3 1,15 * * /home/appuser/Facturacion_Staging/backend/venv/bin/python /home/appuser/Facturacion_Staging/backend/manage.py backup_db
        </code>
      </div>

      {/* Lista de backups */}
      {loading ? (
        <p style={{ color: C.muted, fontSize: "0.85rem" }}>
          <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 6 }}/>Cargando…
        </p>
      ) : !data?.backups?.length ? (
        <p style={{ color: C.muted, fontSize: "0.85rem", textAlign: "center", padding: "30px 0" }}>
          No hay backups aún. Usa el botón "Backup ahora" o configura el cron.
        </p>
      ) : (
        <div style={{ background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...thSt, textAlign: "left" }}>Archivo</th>
                <th style={thSt}>Fecha</th>
                <th style={thSt}>Tamaño</th>
              </tr>
            </thead>
            <tbody>
              {data.backups.map((b, i) => (
                <tr key={b.nombre}>
                  <td style={rowSt}>
                    <i className="fa-solid fa-file-zipper" style={{ color: C.accent, marginRight: 8 }}/>
                    {b.nombre}
                    {i === 0 && (
                      <span style={{ marginLeft: 8, fontSize: "0.7rem", background: "rgba(249,115,22,0.15)",
                        color: C.accent, borderRadius: 4, padding: "1px 6px" }}>más reciente</span>
                    )}
                  </td>
                  <td style={{ ...rowSt, textAlign: "center", color: C.muted }}>
                    {new Date(b.fecha).toLocaleString("es-CR")}
                  </td>
                  <td style={{ ...rowSt, textAlign: "center" }}>
                    <span style={{ color: "#4ade80", fontWeight: 600 }}>{b.tamano}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const TABS = [
  { key: "condominios", label: "Condominios",  icon: "fa-building"    },
  { key: "usuarios",    label: "Usuarios",     icon: "fa-users"       },
  { key: "contadores",  label: "Contadores",   icon: "fa-calculator"  },
  { key: "documentos",  label: "Eliminar Docs", icon: "fa-trash-can"  },
  { key: "backups",     label: "Backups",       icon: "fa-database"   },
];

export default function AdminPage() {
  const [tab, setTab] = useState("condominios");

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <span style={{ color: "#9ca3af" }}>
          <Link to="/"><span style={{ color: "#9ca3af" }}>Inicio </span></Link>/ <span>Admin</span>
        </span>
        <h1 style={{ margin: "8px 0 4px", fontSize: "1.7rem", fontWeight: 800, color: C.text, fontFamily: "'Manrope',sans-serif" }}>
          Panel de Administración
        </h1>
        <p style={{ margin: 0, color: C.muted, fontSize: "0.85rem" }}>
          Gestión global de condominios y usuarios — solo accesible para superadministradores
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 3, marginBottom: 20, background: C.bg2, borderRadius: 12, padding: 4, border: `1px solid ${C.border}` }}>
        {TABS.map(t => {
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{
                flex: 1, padding: "10px 16px", borderRadius: 9,
                background: active ? "rgba(249,115,22,0.1)" : "transparent",
                border: active ? "1px solid rgba(249,115,22,0.2)" : "1px solid transparent",
                color: active ? C.accent : C.muted,
                fontWeight: active ? 700 : 400, fontSize: "0.85rem",
                cursor: "pointer", display: "flex", alignItems: "center",
                justifyContent: "center", gap: 8, transition: "all 0.15s",
                fontFamily: "'Inter',sans-serif",
              }}>
              <i className={`fa-solid ${t.icon}`} style={{ fontSize: "0.8rem" }} />{t.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div style={{ background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 14, padding: "22px 24px" }}>
        <AdminErrorBoundary key={tab}>
          {tab === "condominios" && <CondominiosTab />}
          {tab === "usuarios"    && <UsuariosTab />}
          {tab === "contadores"  && <ContadoresTab />}
          {tab === "documentos"  && <DocumentosTab />}
          {tab === "backups"     && <BackupsTab />}
        </AdminErrorBoundary>
      </div>
    </div>
  );
}
